import config from '@config/env.config';
import { BaseService, type NavigationFilters } from './base-service';
import { PAGINATION } from './constants/service.constants';
import type {
  Ecommerce,
  CheckoutConfig,
  ListCheckoutConfigsResponse
} from '@features/ecommerce/types/ecommerce.types';
import {
  checkoutConfigToEcommerce
} from '@features/ecommerce/utils/ecommerceTransformers';
import { mockEcommerceData } from '@shared/data/mockEcommerce';
import { FilteringUtils } from './utils/filtering.utils';

interface CheckoutConfigQueryParams {
  query?: string;
  sortBy?: string;
  offset?: number;
  size?: number;
  page?: number;
  facets?: string;
  [key: string]: unknown;
}

const DEFAULT_SORT = 'created';
const DEFAULT_FACETS = 'status';

class EcommerceService extends BaseService<Ecommerce, ListCheckoutConfigsResponse, CheckoutConfigQueryParams> {
  protected readonly entityName = 'ecommerce';

  private readonly defaultOrgId: number;
  private readonly defaultAccountId: number;

  constructor() {
    super({
      baseURL: config.endpoints.merchants,
      apiKey: config.apiKey || '',
      entityName: 'ecommerce',
    });
    this.defaultOrgId = config.defaultOrgId;
    this.defaultAccountId = config.defaultAccountId;
  }

  protected buildQueryString(params?: CheckoutConfigQueryParams): string {
    if (!params) return '';

    const queryParams = new URLSearchParams();
    const pageSize = params.size || PAGINATION.DEFAULT_PAGE_SIZE;
    const offset = params.page !== undefined ? params.page * pageSize : params.offset || 0;

    queryParams.append('facets', params.facets || DEFAULT_FACETS);
    queryParams.append('sortby', params.sortBy || DEFAULT_SORT);
    queryParams.append('offset', offset.toString());
    queryParams.append('size', pageSize.toString());

    if (params.query?.trim()) {
      queryParams.append('query', params.query.trim());
    }

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  private getMockCheckoutConfigs(params?: CheckoutConfigQueryParams): ListCheckoutConfigsResponse {
    const pageSize = params?.size || PAGINATION.DEFAULT_PAGE_SIZE;
    const page = params?.page || 0;
    const offset = params?.page !== undefined ? params.page * pageSize : params?.offset || 0;

    let filtered = [...mockEcommerceData];

    if (params?.query?.trim()) {
      const q = params.query.trim().toLowerCase();
      const statusMatch = q.match(/\/\/status=(\w+)\//);
      if (statusMatch) {
        filtered = filtered.filter((e) => e.status === statusMatch[1]);
      } else {
        filtered = filtered.filter(
          (e) =>
            e.name.toLowerCase().includes(q) ||
            e.id.toLowerCase().includes(q) ||
            e.description.toLowerCase().includes(q)
        );
      }
    }

    const totalElements = filtered.length;
    const totalPages = Math.ceil(totalElements / pageSize);
    const results = filtered.slice(offset, offset + pageSize);

    // Map Ecommerce to CheckoutConfig shape for compatibility
    const checkoutResults: CheckoutConfig[] = results.map((e) => ({
      id: e.id,
      accountId: this.defaultAccountId,
      name: e.name,
      description: e.description,
      status: e.status,
      created: e.created,
      updated: e.lastUpdated,
    }));

    return {
      size: pageSize,
      results: checkoutResults,
      page,
      pageSize,
      totalElements,
      totalPages,
      hasNext: offset + pageSize < totalElements,
      hasPrevious: offset > 0,
      facets: FilteringUtils.calculateFacets(mockEcommerceData, ['status', 'type', 'environment']),
    };
  }

  async getCheckoutConfigs(params?: CheckoutConfigQueryParams): Promise<ListCheckoutConfigsResponse> {
    try {
      const queryString = this.buildQueryString(params);
      const url = `/organizations/${this.defaultOrgId}/accounts/${this.defaultAccountId}/checkout-configs/all${queryString}`;

      return await this.apiClient.get<ListCheckoutConfigsResponse>(url, this.defaultHeaders);
    } catch (error) {
      console.warn('[EcommerceService] API unavailable, falling back to mock data:', error);
      return this.getMockCheckoutConfigs(params);
    }
  }

  async getSingleCheckoutConfig(configId: string): Promise<CheckoutConfig> {
    try {
      const url = `/organizations/${this.defaultOrgId}/accounts/${this.defaultAccountId}/checkout-configs/${configId}`;

      return await this.apiClient.get<CheckoutConfig>(url, this.defaultHeaders);
    } catch {
      console.warn('[EcommerceService] API unavailable for getSingle, falling back to mock data');
      const ecomItem = mockEcommerceData.find((e) => e.id === configId);
      if (!ecomItem) throw this.createServiceError('fetch', new Error(`Checkout config ${configId} not found`));
      return {
        id: ecomItem.id,
        accountId: this.defaultAccountId,
        name: ecomItem.name,
        description: ecomItem.description,
        status: ecomItem.status,
        created: ecomItem.created,
        updated: ecomItem.lastUpdated,
      };
    }
  }

  async getEntityById(entityId: string): Promise<Ecommerce> {
    const config = await this.getSingleCheckoutConfig(entityId);
    return checkoutConfigToEcommerce(config);
  }

  getEntitiesForNavigation = async (
    page: number,
    filters: NavigationFilters
  ): Promise<ListCheckoutConfigsResponse> => {
    const params: CheckoutConfigQueryParams = {
      page: page - 1,
      size: filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    };

    if (filters.query?.trim()) {
      params.query = filters.query.trim();
    } else if (filters.statusFilter && filters.statusFilter !== 'all') {
      params.query = `//status=${filters.statusFilter}/`;
    }

    try {
      const response = await this.getCheckoutConfigs(params);
      if (!response) throw this.createServiceError('fetch', new Error('No response received from server'));

      // Transform all configs to Ecommerce type
      const transformedData = response.results.map(checkoutConfigToEcommerce);

      return {
        data: transformedData,
        total: response.totalElements,
        facets: response.facets
      } as unknown as ListCheckoutConfigsResponse;
    } catch {
      console.warn('[EcommerceService] Navigation fallback to mock data');
      const mockResponse = this.getMockCheckoutConfigs(params);
      const transformedData = mockResponse.results.map(checkoutConfigToEcommerce);
      return {
        data: transformedData,
        total: mockResponse.totalElements,
        facets: mockResponse.facets,
      } as unknown as ListCheckoutConfigsResponse;
    }
  }

  async updateEcommerceStatus(id: string, status: 'active' | 'inactive'): Promise<Ecommerce> {
    try {
      const url = `/organizations/${this.defaultOrgId}/accounts/${this.defaultAccountId}/checkout-configs/${id}`;
      const response = await this.apiClient.patch<CheckoutConfig>(url, { status }, this.defaultHeaders);
      return checkoutConfigToEcommerce(response);
    } catch (error) {
      console.warn('[EcommerceService] API unavailable for status update, using mock response:', error);
      const ecomItem = mockEcommerceData.find((e) => e.id === id);
      if (!ecomItem) throw this.createServiceError('update', new Error(`Checkout config ${id} not found`));
      return { ...ecomItem, status };
    }
  }

  async createCheckoutConfig(configData: Partial<CheckoutConfig>): Promise<CheckoutConfig> {
    try {
      const url = `/organizations/${this.defaultOrgId}/accounts/${this.defaultAccountId}/checkout-configs`;
      return await this.apiClient.post<CheckoutConfig>(url, configData, this.defaultHeaders);
    } catch (error) {
      console.warn('[EcommerceService] API unavailable for create, using mock response:', error);
      return {
        id: `mock-${Date.now()}`,
        accountId: this.defaultAccountId,
        name: configData.name || 'New Checkout Config',
        description: configData.description || '',
        status: 'active',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        ...configData,
      } as CheckoutConfig;
    }
  }

  async updateCheckoutConfig(id: string, configData: Partial<CheckoutConfig>): Promise<CheckoutConfig> {
    try {
      const url = `/organizations/${this.defaultOrgId}/accounts/${this.defaultAccountId}/checkout-configs/${id}`;
      return await this.apiClient.put<CheckoutConfig>(url, configData, this.defaultHeaders);
    } catch (error) {
      console.warn('[EcommerceService] API unavailable for update, using mock response:', error);
      return {
        id,
        accountId: this.defaultAccountId,
        status: 'active',
        created: new Date().toISOString(),
        updated: new Date().toISOString(),
        ...configData,
      } as CheckoutConfig;
    }
  }

  async saveDraft(configData: Partial<CheckoutConfig>): Promise<CheckoutConfig> {
    return this.createCheckoutConfig({
      ...configData,
      status: 'inactive',
      isDraft: true,
      draftSavedAt: new Date().toISOString()
    });
  }

  async getDrafts(): Promise<CheckoutConfig[]> {
    const response = await this.getCheckoutConfigs({ query: '//isDraft=true/' });
    return response.results;
  }
}

export const ecommerceService = new EcommerceService();
